import type { Dayjs } from 'dayjs';

import type {
  CHART_CALCULATION,
  CHART_COLOR_SCALE,
  CHART_COMPARE_TERM_TYPE,
  CHART_FIELD,
  CHART_FREQUENCY,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_PERIOD_RELATIVE,
  CHART_SHARE_CALCULATION_TYPE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import type { FeedbackSentiment } from '@/types/feedbackConst';

import {
  applyStateChange,
  applyStateChangeWithSideEffects,
} from './chartStateReducer';
import * as rules from './chartStateRules';

const useChartStateAction = (chartInfo: ChartContextProps) => {
  const { state, setState, setSelectedField } = chartInfo;

  const onChartTypeChange = (value: CHART_TYPE) => {
    const newState = applyStateChange(state, rules.chartTypeChangeRule, value);
    if (newState) {
      setState(newState);
      setSelectedField(newState.selected_field);
    }
  };

  const onTermTypeChange = (type: CHART_TERM_TYPE) => {
    const newState = applyStateChange(state, rules.termTypeChangeRule, type);
    if (newState) {
      setState(newState);
    }
  };

  const onFieldChange = (value: CHART_FIELD, chartSeriesIndex?: number) => {
    const newState = applyStateChange(state, rules.fieldChangeRule, {
      field: value,
      chartSeriesIndex,
    });
    if (newState) {
      setState(newState);
      setSelectedField(newState.selected_field);
    }
  };

  const onFieldItemChange = (
    values: StringNumberValue[],
    chartSeriesIndex?: number,
  ) => {
    const newState = applyStateChange(state, rules.fieldItemChangeRule, {
      values,
      chartSeriesIndex,
    });
    if (newState) {
      setState(newState);
      setSelectedField(newState.selected_field);
    }
  };

  const onMetricChange = (value: CHART_METRIC, chartSeriesIndex: number) => {
    const newState = applyStateChange(state, rules.metricChangeRule, {
      metric: value,
      chartSeriesIndex,
    });
    if (newState) {
      setState(newState);
    }
  };

  const onCalculationChange = (
    value: CHART_CALCULATION,
    chartSeriesIndex: number,
  ) => {
    const newState = applyStateChange(state, rules.calculationChangeRule, {
      calculation: value,
      chartSeriesIndex,
    });
    if (newState) {
      setState(newState);
    }
  };

  const onAddCompareClick = () => {
    const newState = applyStateChange(state, rules.addCompareRule, undefined);
    if (newState) {
      setState(newState);
    }
  };

  const onRemoveCompareClick = () => {
    const newState = applyStateChange(
      state,
      rules.removeCompareRule,
      undefined,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onCompareTermTypeChange = (value: CHART_COMPARE_TERM_TYPE) => {
    const newState = applyStateChange(
      state,
      rules.compareTermTypeChangeRule,
      value,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onCustomCompareStartChange = (start_date: Dayjs) => {
    const newState = applyStateChange(
      state,
      rules.customCompareStartChangeRule,
      start_date,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onCustomTermChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const newState = applyStateChange(state, rules.customTermChangeRule, dates);
    if (newState) {
      setState(newState);
    }
  };

  const onFrequencyChange = (value: CHART_FREQUENCY) => {
    const newState = applyStateChange(state, rules.frequencyChangeRule, value);
    if (newState) {
      setState(newState);
    }
  };

  const onTermUnitChange = (unit: CHART_TERM_UNIT) => {
    const newState = applyStateChange(state, rules.termUnitChangeRule, unit);
    if (newState) {
      setState(newState);
    }
  };

  const onTermRelativeChange = (value: CHART_PERIOD_RELATIVE) => {
    const newState = applyStateChange(
      state,
      rules.termRelativeChangeRule,
      value,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onTermValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    const newState = applyStateChange(state, rules.termValueChangeRule, value);
    if (newState) {
      setState(newState);
    }
  };

  const onFeedbackFilterChange = (filter?: StructuredFilter) => {
    const newState = applyStateChange(
      state,
      rules.feedbackFilterChangeRule,
      filter,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onWordCloudDatasetChange = (fieldType: CHART_FIELD) => {
    const newState = applyStateChange(
      state,
      rules.wordCloudDatasetChangeRule,
      fieldType,
    );
    if (newState) {
      setState(newState);
    }
  };

  const resetFeedbackFilter = () => {
    const newState = applyStateChange(
      state,
      rules.feedbackFilterChangeRule,
      undefined,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onGraphChange = (value: CHART_GRAPH) => {
    const newState = applyStateChange(state, rules.graphChangeRule, value);
    const appliedImmediately = applyStateChangeWithSideEffects(
      state,
      rules.graphChangeRule,
      value,
      setState,
    );
    if (appliedImmediately && newState) {
      setSelectedField(newState.selected_field);
    }
  };

  const onGraphDirectionChange = (value: CHART_GRAPH_DIRECTION) => {
    const newState = applyStateChange(
      state,
      rules.graphDirectionChangeRule,
      value,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onViewTypeChange = (
    chartSeriesIndex: number,
    value: CHART_METRIC_VIEW_TYPE,
  ) => {
    const newState = applyStateChange(state, rules.viewTypeChangeRule, {
      chartSeriesIndex,
      viewType: value,
    });
    if (newState) {
      setState(newState);
    }
  };

  const onScaleOptionChange = (
    chartSeriesIndex: number,
    scale?: ChartScale,
  ) => {
    const newState = applyStateChange(state, rules.scaleOptionChangeRule, {
      chartSeriesIndex,
      scale,
    });
    if (newState) {
      setState(newState);
    }
  };

  const onColorScaleChange = (
    chartSeriesIndex: number,
    value?: CHART_COLOR_SCALE,
  ) => {
    const newState = applyStateChange(state, rules.colorScaleChangeRule, {
      chartSeriesIndex,
      colorScale: value,
    });
    if (newState) {
      setState(newState);
    }
  };

  const onHistoricalChange = (value: boolean) => {
    const newState = applyStateChange(state, rules.historicalChangeRule, value);
    if (newState) {
      setState(newState);
    }
  };

  const onShareCalculationTypeChange = (
    chartSeriesIndex: number,
    value: CHART_SHARE_CALCULATION_TYPE,
  ) => {
    const newState = applyStateChange(
      state,
      rules.shareCalculationTypeChangeRule,
      {
        chartSeriesIndex,
        shareCalculationType: value,
      },
    );
    if (newState) {
      setState(newState);
    }
  };

  const onShareCellCalculationSegmentChange = (
    chartSeriesIndex: number,
    value: FeedbackSentiment,
  ) => {
    const newState = applyStateChange(
      state,
      rules.shareCellCalculationSegmentChangeRule,
      {
        chartSeriesIndex,
        segment: value,
      },
    );
    if (newState) {
      setState(newState);
    }
  };

  const onDatasetSwitch = () => {
    const newState = applyStateChange(
      state,
      rules.datasetSwitchRule,
      undefined,
    );
    if (newState) {
      setState(newState);
    }
  };

  const onShowRowTotalChange = (value: boolean, chartSeriesIndex: number) => {
    const newState = applyStateChange(state, rules.showRowTotalChangeRule, {
      value,
      chartSeriesIndex,
    });
    if (newState) {
      setState(newState);
    }
  };

  return {
    onChartTypeChange,
    onTermTypeChange,
    onFieldChange,
    onFieldItemChange,
    onCalculationChange,
    onMetricChange,
    onAddCompareClick,
    onRemoveCompareClick,
    onCompareTermTypeChange,
    onCustomTermChange,
    onFrequencyChange,
    onTermUnitChange,
    onTermRelativeChange,
    onTermValueChange,
    onFeedbackFilterChange,
    onWordCloudDatasetChange,
    resetFeedbackFilter,
    onGraphChange,
    onGraphDirectionChange,
    onViewTypeChange,
    onScaleOptionChange,
    onColorScaleChange,
    onHistoricalChange,
    onCustomCompareStartChange,
    onShareCalculationTypeChange,
    onShareCellCalculationSegmentChange,
    onDatasetSwitch,
    onShowRowTotalChange,
  };
};

export default useChartStateAction;
